import { createContext, useState, FC, useEffect, useContext } from 'react';
import apiUrl from '../../assets/apiUrl';
import AuthContext from '../Auth';
import { FileSystemContext } from '../FileSystem';
import FilesMethods from '../FileSystem/FilesMethods';
import zip from 'jszip';
import Protonfile from './ExposedObject';
import file from '../../common/file';

interface IPluginsContext {
  getPluginWithCache: (file_uid: string) => Promise<void | Response>;
}
const defaultState = {
  getPluginWithCache: () =>
    new Promise<void | Response>((resolve) => resolve()),
};

const PluginsContext = createContext<IPluginsContext>(defaultState);
const systemFolders = ['.thumbnails', '.plugins'];

export const PluginsProvider: FC = ({ children }) => {
  const { isAuthenticated, isLoading } = useContext(AuthContext);
  const {
    isLoading: isLoadingFs,
    useFiles,
    useFolders,
    useFilesMethods,
  } = useContext(FileSystemContext);
  const [files] = useFiles();
  const [folders] = useFolders();
  const [isLoaded, setIsLoaded] = useState(false);
  const getPluginWithCache = async (file_uid: string) => {
    const cache = await caches.open('plugins');

    let response = await cache.match(file_uid);

    if (response === undefined) {
      try {
        await cache.put(
          file_uid,
          await fetch(
            await FilesMethods.getSignedUrl(
              `${apiUrl}/file?file_uid=${file_uid}`
            )
          )
        );
        response = await cache.match(file_uid);
        console.log('Using fresh version');
        return response;
      } catch (error) {
        return console.log('Failed to load plugin file');
      }
    }
    console.log('Using cached version');
    return response;
  };
  const { getSignedUrl } = useFilesMethods();
  useEffect(() => {
    if (!isAuthenticated && !isLoading && !isLoadingFs) return;
    if (isLoaded) return;
    console.log('Plugins provider loaded');
    (async () => {
      // load all plugins from the ".plugins" folder
      const pluginFolder = folders.find(
        (e) => e.title === '.plugins' && e.parent_folder === null
      );
      if (!pluginFolder) return;
      const pluginFiles = files.filter(
        (e) => e.folder_uid === pluginFolder.folder_uid
      );
      console.log(`Loading ${pluginFiles.length} plugins`, pluginFiles);

      window.Protonfile = new Protonfile();
      window.Protonfile.File.getSignedUrl = async (file: file | string) => {
        let file_uid: string;
        if (typeof file === 'string') {
          file_uid = file;
        } else {
          file_uid = file.file_uid;
        }
        const url = await getSignedUrl(`${apiUrl}/file?file_uid=${file_uid}`);
        return url;
      };

      for (const file of pluginFiles) {
        const cachedFile = await getPluginWithCache(file.file_uid);
        if (!cachedFile) return;
        const pluginFile = await zip.loadAsync(cachedFile.blob());
        const scriptBlob = await pluginFile.file('script.js')?.async('blob');
        if (!scriptBlob) return console.log('Script invalid');

        const script = document.createElement('script');
        script.src = URL.createObjectURL(scriptBlob);
        script.async = true;
        script.onload = () => console.log('Script injected');

        document.body.appendChild(script);
      }
      setIsLoaded(true);
    })();
  }, [isAuthenticated, isLoading, folders]);
  return (
    <PluginsContext.Provider
      value={{
        getPluginWithCache,
      }}
    >
      {children}
    </PluginsContext.Provider>
  );
};

export { PluginsContext, systemFolders };
